import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SuggestedVacancy, ScoringFactorDetail, ScoringMetadata } from '../../interfaces/market-vacancy';
import { TranslateService } from '@ngx-translate/core';
import { MarketVacancyEventsService, VacancyImpressionEvent } from '../../shared/market-vacancy-events.service';

@Component({
  selector: 'bst-suggested-vacancies',
  templateUrl: './suggested-vacancies.component.html',
  styleUrls: ['./suggested-vacancies.component.scss'],
  standalone: false
})
export class SuggestedVacanciesComponent implements OnChanges {

  @Input() matches: SuggestedVacancy[] = [];
  @Input() loading: boolean = false;
  @Input() targetUserId?: number;
  @Output() vacancyClick = new EventEmitter<number>();

  expanded: boolean = false;
  defaultCount: number = 5;

  private lastPostedKey: string | null = null;

  constructor(
    private translate: TranslateService,
    private eventsService: MarketVacancyEventsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['matches'] && !this.loading) {
      this.postImpressions();
    }
  }

  get visibleMatches(): SuggestedVacancy[] {
    return this.expanded ? this.matches : this.matches.slice(0, this.defaultCount);
  }

  get hasMore(): boolean {
    return this.matches.length > this.defaultCount;
  }

  // Royal intensity ramp (no traffic-light green/amber per DESIGN.md): quality
  // is encoded by how deep the royal is, not by hue.
  public getScoreColor(score: number | null): string {
    if (score === null) return 'basic';
    if (score >= 80) return 'high';
    if (score >= 50) return 'med';
    if (score > 0) return 'low';
    return 'basic';
  }

  public getFactors(match: SuggestedVacancy): { key: string; label: string; score: number; applicable: boolean; notes: string }[] {
    const details: ScoringMetadata = match.match_details;
    if (!details?.scoring_factors) return [];

    return Object.entries(details.scoring_factors)
      .map(([key, factor]: [string, ScoringFactorDetail]) => ({
        key,
        label: this.translate.instant(`scoring.factors.${key}`) || key,
        score: factor.score || 0,
        applicable: factor.applicable,
        notes: factor.notes || '',
      }))
      .filter(f => f.applicable);
  }

  public getFactorBarColor(score: number): string {
    if (score >= 0.8) return 'high';
    if (score >= 0.5) return 'med';
    if (score > 0) return 'low';
    return 'basic';
  }

  public onCardClick(match: SuggestedVacancy): void {
    this.vacancyClick.emit(match.MarketVacancy_id);
  }

  private postImpressions(): void {
    if (!this.matches?.length) return;
    const visible = this.visibleMatches ?? this.matches;
    const events: VacancyImpressionEvent[] = visible
      .map((m, i) => ({
        MarketVacancy_id: m.vacancy?.id as number,
        event_type: 'impression' as const,
        rank: i,
        // A demoted pair has no score on the wire; null lets the server fill the stored one.
        score: m.match_demoted ? null : (m.score ?? null),
        // Normalize to the full shape so F2's per-flag buckets can rely on
        // booleans being present. Pre-deploy cached responses or upstream
        // changes that drop the field default to "off" rather than missing.
        metadata: {
          flag_state: {
            semantic_skill_fallback: m.flag_state?.semantic_skill_fallback ?? false,
            hard_skills_evidence: m.flag_state?.hard_skills_evidence ?? false,
          },
          ...(typeof this.targetUserId === 'number' ? { target_user_id: this.targetUserId } : {}),
        },
      }))
      .filter(e => typeof e.MarketVacancy_id === 'number' && e.MarketVacancy_id > 0);

    if (!events.length) return;

    const key = JSON.stringify({ ids: events.map(e => e.MarketVacancy_id), target: this.targetUserId ?? null });
    if (key === this.lastPostedKey) return;
    this.lastPostedKey = key;

    this.eventsService.postImpressions(events);
  }
}
